// ANCHOR React
import { memo } from 'react';

// ANCHOR Next
import Link from 'next/link';

// ANCHOR React Icons
import { Icon } from 'react-icons-kit';
import { envelope } from 'react-icons-kit/fa/envelope';
import { phone } from 'react-icons-kit/fa/phone';
import { globe } from 'react-icons-kit/fa/globe';
import { group } from 'react-icons-kit/fa/group';
import { addressCard } from 'react-icons-kit/fa/addressCard';

// ANCHOR Base
import { LabelMedium, LabelSmall } from 'baseui/typography';
import { Block } from 'baseui/block';
import { Avatar } from 'baseui/avatar';
import { withStyle } from 'baseui';
import { StyledLink } from 'baseui/link';

// ANCHOR Models
import { ProviderData } from '@lyon/scoped-models/ProviderData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { MobileView } from '@lyon/scoped-models/MobileView';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';
import { Skeleton } from '@lyon/components/utils/Skeleton';
import { BoxSkeleton } from '@lyon/components/utils/BoxSkeleton';

// ANCHOR Hooks
import { useAvatar } from '@lyon/utils/hooks/extras/useAvatar';

// ANCHOR Utils
import {
  ucarecdn,
  ucarecdnScaleCropOptimize,
} from '@lyon/utils/ucarecdn';

// ANCHOR Styles
import {
  AVATAR_SIZE,
  BLOCK,
  SKELETON,
  HEADER,
  NORMAL,
  NORMAL_BASE,
  CENTER,
  MEDIUM,
  ICON,
} from './styles';

const AvatarSkeleton = withStyle(Skeleton, SKELETON);

const ProviderAvatar = constant(() => {
  const [displayPhotoUuid, name] = ProviderData.useSelectors(({ data }) => [
    data?.displayPhotoUuid,
    data?.name,
  ]);

  const avatar = useAvatar(name);

  if (name) {
    return (
      <StyledLink
        href={ucarecdn(displayPhotoUuid)}
        target="_blank"
        rel="noreferrer noopener"
      >
        <Avatar
          name={name}
          size={AVATAR_SIZE}
          src={displayPhotoUuid
            ? ucarecdnScaleCropOptimize('196x196', displayPhotoUuid)
            : avatar}
        />
      </StyledLink>
    );
  }

  return <AvatarSkeleton />;
});

const ProviderDetails = constant(() => {
  const [name, id] = ProviderData.useSelectors(({ data }) => [
    data?.name,
    data?.id,
  ]);
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);

  if (name && id) {
    return (
      <Block
        display="grid"
        justifyContent="center"
        marginLeft="16px"
        marginRight="16px"
      >
        <LabelMedium overrides={HEADER}>
          {name}
        </LabelMedium>
        <LabelSmall marginTop="8px" overrides={CENTER}>
          {
            isProvider
              ? (
                <Link href={`/provider/${id}/manage`} passHref>
                  <StyledLink>
                    Edit Page
                  </StyledLink>
                </Link>
              )
              : 'Instructor'
          }
        </LabelSmall>
      </Block>
    );
  }

  return (
    <Block display="grid" justifyContent="center">
      <BoxSkeleton
        height="22px"
        width="100%"
        animation
        rows={4}
        rowSpacing="16px"
      />
    </Block>
  );
});

interface IContactItemProps {
  icon: any;
  value?: string;
  label: string;
  isAvailable: boolean;
  isUrl?: boolean;
}

const ProviderContactItem = memo(({
  icon, value, isAvailable, label, isUrl,
}: IContactItemProps) => {
  const isMobile = MobileView.useSelector((state) => state.isMobile);

  if (value && isAvailable) {
    return (
      <Block
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent={isMobile ? 'center' : undefined}
        marginLeft="24px"
        marginRight="24px"
      >
        <Block overrides={ICON}>
          <Icon size={20} icon={icon} />
          {isMobile && (
            <LabelSmall overrides={MEDIUM} marginLeft="16px">
              {label}
            </LabelSmall>
          )}
        </Block>
        {
          isUrl
            ? (
              <StyledLink
                href={value}
                target="_blank"
                rel="noreferrer noopener"
                $style={NORMAL_BASE}
              >
                {value}
              </StyledLink>
            )
            : <LabelSmall overrides={NORMAL}>{value}</LabelSmall>
        }
      </Block>
    );
  }

  return null;
});

const ProviderContacts = constant(() => {
  const provider = ProviderData.useSelector(({ data }) => data);
  const isMobile = MobileView.useSelector((state) => state.isMobile);

  if (provider) {
    const {
      email, contactNumber, address, website, lyonCommunity,
    } = provider;

    return (
      <Block
        width="100%"
        marginTop="48px"
        display="grid"
        gridRowGap="12px"
      >
        <ProviderContactItem
          label="Email:"
          icon={envelope}
          value={email}
          isAvailable={!!email}
        />
        <ProviderContactItem
          label="Contact Number:"
          icon={phone}
          value={contactNumber}
          isAvailable={!!contactNumber}
        />
        <ProviderContactItem
          label="Business Address:"
          icon={addressCard}
          value={address}
          isAvailable={!!address}
        />
        <ProviderContactItem
          label="Website:"
          icon={globe}
          value={website}
          isAvailable={!!website}
          isUrl
        />
        {!!lyonCommunity && (
          <Block
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems="center"
            justifyContent={isMobile ? 'center' : undefined}
            marginLeft="24px"
            marginRight="24px"
          >
            <Block overrides={ICON}>
              <Icon size={20} icon={group} />
              <StyledLink
                href={lyonCommunity}
                target="_blank"
                rel="noreferrer noopener"
                $style={NORMAL_BASE}
              >
                <LabelSmall overrides={MEDIUM} marginLeft={isMobile ? '16px' : '12px'}>
                  LYON Community
                </LabelSmall>
              </StyledLink>
            </Block>
          </Block>
        )}
      </Block>
    );
  }

  return null;
});

export const LyonProviderSidebar = constant(() => (
  <Block>
    <Block overrides={BLOCK}>
      <Block
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <ProviderAvatar />
        <ProviderDetails />
        <ProviderContacts />
      </Block>
    </Block>
  </Block>
));
